import { useTranslation } from 'react-i18next';
import type { ColumnConfig } from '@/components/ui';
import { AccountsTable as SharedAccountsTable } from './shared/AccountsTable';
import { IdentityCell, StatusChip, type CellTone } from './shared/cells';
import { isPendingFirstLogin, type AccountSortDirection, type AccountSortField, type KeycloakAccountRow } from './types';

/**
 * EAA-1820: not a component, so it takes the already-resolved `t` from its
 * caller instead of calling `useTranslation` itself (hooks may only run in
 * components/hooks).
 */
function lastLoginLabel(
    lastLogin: number | null | undefined,
    available: boolean,
    t: (key: string) => string
): string {
    if (!available) return t('console.accounts.table.lastLoginUnavailable');
    if (!lastLogin) return t('console.accounts.table.lastLoginNotRecorded');
    return new Date(lastLogin).toLocaleDateString('vi-VN');
}

function PlacementCell({ placement }: { placement?: KeycloakAccountRow['placement'] }) {
    const { t } = useTranslation();
    if (!placement) {
        return (
            <span className="portal-accounts-unassigned">
                {t('console.accounts.table.placementUnassigned')}
            </span>
        );
    }
    return (
        <span className="acc-placement">
            <span className="acc-placement__where">
                {placement.organization_name}
                {placement.level_name && ` · ${placement.level_name}`}
            </span>
            {placement.count > 1 && (
                <span className="acc-placement__more">
                    {t('console.accounts.table.placementMoreCount', { count: placement.count - 1 })}
                </span>
            )}
        </span>
    );
}

function AppRolesCell({ row }: { row: KeycloakAccountRow }) {
    const { t } = useTranslation();
    const roles = row.app_roles ?? [];
    if (roles.length > 0) {
        return (
            <span className="acc-approles">
                {roles.map((role) => (
                    <code className="acc-approle" key={role}>
                        {role}
                    </code>
                ))}
            </span>
        );
    }
    if (!row.placement) return <span className="portal-accounts-unavailable-cell">—</span>;
    return <span className="portal-accounts-unassigned">{t('console.accounts.table.noPermission')}</span>;
}

interface Props {
    rows: KeycloakAccountRow[];
    selected: Set<string>;
    onSelectedChange: (next: Set<string>) => void;
    sortField: AccountSortField;
    sortDirection: AccountSortDirection;
    onSort: (field: AccountSortField) => void;
    onViewDetail: (row: KeycloakAccountRow) => void;
    onResetOne: (row: KeycloakAccountRow) => void;
    onDeleteOne: (row: KeycloakAccountRow) => void;
    /** EAA-1820 (frame 84): opens the "Gán Level" dialog for this identity. */
    onAssignLevel: (row: KeycloakAccountRow) => void;
    /** EAA-1820 (frame 86): opens the disable/re-enable dialog for this identity. */
    onDeactivate: (row: KeycloakAccountRow) => void;
    canReset: boolean;
    canDelete: boolean;
    /** EAA-1820: coarse proxy for "may grant Level assignments" — see
     *  `useAssignLevelToAccount`'s docstring for why there is no single
     *  capability string to check upfront on this realm-wide screen. */
    canAssignLevel: boolean;
    /** `console.accounts.lock` — gates the enable/disable toggle. */
    canLock: boolean;
    lastLoginAvailable: boolean;
}

/**
 * EAA-1820: the realm accounts columns, rendered through the ONE shared
 * `shared/AccountsTable`. Column CONTENT (placement, app roles, status/last-
 * login derivation) stays here because it is realm-specific domain logic; the
 * table chrome (DataTable, selection, sort caret, "Xem" + kebab, single scroll
 * region) is now shared with the org screen's `ScopedAccountsTable.tsx`.
 */
export function AccountsTable({
    rows,
    selected,
    onSelectedChange,
    sortField,
    sortDirection,
    onSort,
    onViewDetail,
    onResetOne,
    onDeleteOne,
    onAssignLevel,
    onDeactivate,
    canReset,
    canDelete,
    canAssignLevel,
    canLock,
    lastLoginAvailable,
}: Props) {
    const { t } = useTranslation();
    const columns: ColumnConfig<KeycloakAccountRow>[] = [
        {
            key: 'username',
            title: t('console.accounts.table.columnAccount'),
            sorter: true,
            render: (_value, row) => (
                <IdentityCell primary={row.username} secondary={row.email} onClick={() => onViewDetail(row)} />
            ),
        },
        {
            key: 'placement',
            title: t('console.accounts.table.columnPlacement'),
            render: (_value, row) => <PlacementCell placement={row.placement} />,
        },
        {
            key: 'app_roles',
            title: t('console.accounts.table.columnAppRoles'),
            render: (_value, row) => <AppRolesCell row={row} />,
        },
        {
            key: 'status',
            title: t('console.accounts.table.columnStatus'),
            sorter: true,
            render: (_value, row) => {
                const tone: CellTone = !row.enabled ? 'off' : isPendingFirstLogin(row) ? 'warn' : 'ok';
                const label = !row.enabled
                    ? t('console.accounts.table.statusLocked')
                    : isPendingFirstLogin(row)
                      ? t('console.accounts.table.statusPendingFirstLogin')
                      : t('console.accounts.table.statusActive');
                return <StatusChip tone={tone} label={label} />;
            },
        },
        {
            key: 'last_login',
            title: t('console.accounts.table.columnLastLogin'),
            render: (_value, row) => lastLoginLabel(row.last_login, lastLoginAvailable, t),
        },
    ];

    return (
        <SharedAccountsTable<KeycloakAccountRow>
            rows={rows}
            columns={columns}
            rowKey="sub"
            selectedKeys={[...selected]}
            onSelectedKeysChange={(keys) => onSelectedChange(new Set(keys))}
            sortedInfo={{ field: sortField, order: sortDirection === 'asc' ? 'ascend' : 'descend' }}
            onSortChange={(ordering) => onSort(ordering.replace(/^-/, '') as AccountSortField)}
            onViewDetail={onViewDetail}
            rowActionsAriaLabel={(row) =>
                t('console.accounts.table.rowActionsAriaLabel', { username: row.username })
            }
            rowActions={(row) => [
                { key: 'detail', label: t('console.accounts.table.viewDetailAction'), onSelect: () => onViewDetail(row) },
                ...(canAssignLevel
                    ? [{ key: 'assign-level', label: t('console.accounts.table.assignLevelAction'), onSelect: () => onAssignLevel(row) }]
                    : []),
                ...(canReset
                    ? [{ key: 'reset', label: t('console.accounts.table.resetAction'), onSelect: () => onResetOne(row) }]
                    : []),
                ...(canDelete
                    ? [
                          {
                              key: 'delete',
                              label: t('console.accounts.table.deleteAction'),
                              danger: true,
                              onSelect: () => onDeleteOne(row),
                          },
                      ]
                    : []),
                ...(canLock
                    ? [
                          {
                              key: 'deactivate',
                              label: row.enabled
                                  ? t('console.accounts.table.deactivateAction')
                                  : t('console.accounts.table.reactivateAction'),
                              danger: row.enabled,
                              onSelect: () => onDeactivate(row),
                          },
                      ]
                    : []),
            ]}
            emptyText={t('console.accounts.table.emptyText')}
        />
    );
}

export default AccountsTable;
